import React, { useState, useEffect } from 'react';
import { FaArrowRight, FaChevronLeft, FaChevronRight, FaPlus, FaSearch, FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './Dashboard.css';
import config from './config.json';
import Bot from './bot';

export default function Dashboard({ name, isModalOpen, isRegisterModalOpen, setIsRegisterModalOpen, setIsModalOpen, setIsLoggedIn, setUsername, setAccessKey, accessKey, remainingCredits }) {
  const [jobRoles, setJobRoles] = useState([]);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editableJob, setEditableJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const itemsPerPage = 5;
  const navigate = useNavigate();

  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);

  const [deletedJobIds, setDeletedJobIds] = useState([]);

  const [dashboardStats, setDashboardStats] = useState({
    candidate_shortlisted_count: 0,
    job_role_created_count: 0,
    resume_uploaded_count: 0
  });

  useEffect(() => {
    // Load Google Analytics
    const script1 = document.createElement('script')
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-V32YGHXF75'
    script1.async = true
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-V32YGHXF75');
    `
    document.head.appendChild(script2)

    // Cleanup function
    return () => {
      document.head.removeChild(script1)
      document.head.removeChild(script2)
    }
  }, []) // Empty dependency array ensures this runs once on mount

  const fetchJobRoles = async () => {
    try {
      const storedEmail = localStorage.getItem("email");
      const storedAccessToken = localStorage.getItem("access_token");
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/job-description-matcher/fetch-job-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedAccessToken}`,
        },
        body: JSON.stringify({ UserID: storedEmail }),
      });

      const result = await response.json();
      if (result.status === 200) {
        setJobRoles(result.data);
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Error fetching job roles:', error);
    }
  };

  const fetchHistoryData = async () => {
    const storedEmail = localStorage.getItem('email');
    const storedAccessToken = localStorage.getItem('access_token');

    if (!storedEmail || !storedAccessToken) {
      setError('You are not logged in. Please log in to access your results.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/retrieve-forensics-jobs-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedAccessToken}`
        },
        body: JSON.stringify({
          email: storedEmail,
          page_number: 1
        })
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setHistoryData(data.data.mayamaya_lite);
      } else {
        setError(data.message || 'Failed to fetch data.');
      }
    } catch (error) {
      setError('Failed to fetch data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditPopup = (job) => {
    setEditableJob(job);
    setIsEditPopupOpen(true);
  };

  const closeEditPopup = () => {
    setIsEditPopupOpen(false);
    setEditableJob(null);
  };

  const convertMonthsToYears = (months) => {
    console.log(`months ${months}`);
    if (typeof months !== 'number') {
      console.error('Invalid input to convertMonthsToYears:', months);
      return "0+ years";
    }
    if (months < 12) return "0+ years";
    if (months < 36) return "1+ years";
    if (months < 60) return "3+ years";
    if (months < 120) return "5+ years";
    return "10+ years";
  };
  
  const yearsToMonths = (years) => {
    console.log(`years ${years}`);
    if (typeof years !== 'string') {
      console.error('Invalid input to YearsToMonths:', years);
      return "0+ years";
    }
    switch (years) {
     
      case "0+ years": return 0;
      case "1+ years": return 12;
      case "3+ years": return 36;
      case "5+ years": return 60;
      case "10+ years": return 120;
      default: return 0;
    }
  };

  const capitalizeFirstLetter = (text) => text.charAt(0).toUpperCase() + text.slice(1);

  const handleSkillInputKeyDownEdit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkillEdit();
    }
  };

  const handleAddSkillEdit = () => {
    if (skillInput && editableJob) {
      if (!editableJob.job_skills.includes(skillInput)) {
        setEditableJob({
          ...editableJob,
          job_skills: [...editableJob.job_skills, skillInput]
        });
      }
      setSkillInput('');
    }
  };

  const handleDeleteSkill = (skillToDelete) => {
    setEditableJob({
      ...editableJob,
      job_skills: editableJob.job_skills.filter(skill => skill !== skillToDelete)
    });
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername(""); 
    setAccessKey("");
    localStorage.removeItem("access_token"); 
    localStorage.removeItem("email");
    localStorage.removeItem("uploadedFiles");
    localStorage.removeItem("driveLink");
    window.location.reload();
  };

  const fetchRecentActivities = async () => {
    const storedEmail = localStorage.getItem("email");
    const storedAccessToken = localStorage.getItem("access_token");

    try {
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/user/recent-activity?user_email=${storedEmail}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedAccessToken}`,
        },
      });

      const result = await response.json();
      if (result.status === 200) {
        setRecentActivities(result.data.recent_activity);
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    }
  };

  const fetchDashboardStats = async () => {
    const storedEmail = localStorage.getItem("email");
    const storedAccessToken = localStorage.getItem("access_token");

    try {
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/user/dashboard-stats?user_email=${storedEmail}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedAccessToken}`,
        },
      });

      const result = await response.json();
      if (result.status === 200) {
        setDashboardStats(result.data);
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  // Combine historyData and jobRoles
  const combinedData = [
    ...historyData.filter(historyEntry => 
      jobRoles.some(job => job.job_title === historyEntry.jdm.job_title)
    ),
    ...jobRoles.filter(job => 
      !historyData.some(historyEntry => historyEntry.jdm.job_title === job.job_title)
    )
  ];

// Aggregate items first
const aggregatedItems = combinedData.reduce((acc, entry) => {
  const jobTitle = entry.jdm?.job_title || entry.job_title;
  const jobId = entry.job_id || entry.jdm?.job_id;

  // Skip deleted jobs
  if (deletedJobIds.includes(jobId)) return acc;

  // Check if the job title already exists in the accumulator
  const existingEntry = acc.find(item => item.jobTitle === jobTitle);

  if (existingEntry) {
    // Sum up num_files and num_shortlisted
    existingEntry.numFiles += entry.forensics?.num_files || 0;
    existingEntry.numShortlisted += entry.jdm?.num_shortlisted || 0;
  } else {
    // Add a new unique entry
    acc.push({
      jobTitle,
      description: entry.description || '',
      createdDate: entry.forensics?.created_date || entry.created_date,
      numFiles: entry.forensics?.num_files || 0,
      numShortlisted: entry.jdm?.num_shortlisted || 0,
      runId: entry.jdm?.run_id,
      folderName: entry.forensics?.folder_name,
    });
  }

  return acc;
}, []);

// Filter aggregated items based on search query
const filteredAggregatedItems = aggregatedItems.filter(item =>
  item.job_title
);

// Recalculate pagination based on filtered aggregated items
const totalFilteredItems = filteredAggregatedItems.length;
const totalFilteredPages = Math.ceil(totalFilteredItems / itemsPerPage);
const indexOfLastFilteredItem = currentPage * itemsPerPage;
const indexOfFirstFilteredItem = indexOfLastFilteredItem - itemsPerPage;
const currentFilteredItems = filteredAggregatedItems.slice(indexOfFirstFilteredItem, indexOfLastFilteredItem);

const handlePageChange = (pageNumber) => {
  setCurrentPage(pageNumber);
};

  const handleShortlistClick = async () => {
    navigate("/job-description")
  };

  const handleViewClick = async () => {
    navigate("/folder-manager");
  }

  const handleJobViewClick = async () => {
    navigate("/job-description-page");
  }

  const handleEditJobDescription = async () => {
    // Implement the logic to save the edited job description
    console.log("Saving edited job description:", editableJob);
    closeEditPopup();
  };

  const [folders, setFolders] = useState([]);

  const fetchFolders = async (email, accessKey) => {

    const emailId = localStorage.getItem("email");
    const access_key = localStorage.getItem("access_token");

    try {
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/retrieve-folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${access_key}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailId }),
      });

      const data = await response.json();
      if (data && data.data && Array.isArray(data.data.folders)) {
        const completedFolders = data.data.folders.filter(folder => folder.resume_parser_status === "completed");
        setFolders(completedFolders); // Store the full folder objects
      } else {
        console.error("Invalid data format:", data);
        setFolders([]);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      setFolders([]);
    }
  };

  const currentFolders = [...folders]
  .reverse()
  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleBotLoad = (hubspot) => {
    //console.log("HubSpot chatbot loaded successfully:", hubspot);
    // You can add additional logic to interact with the HubSpot bot here if needed
  };  

  useEffect(() => {
    fetchJobRoles();
    fetchHistoryData();
    fetchFolders();
    fetchRecentActivities();
    fetchDashboardStats();
  }, []);

  return (
    <div className='rd-body'>
      <Header 
        name={name}
        setIsLoggedIn={setIsLoggedIn}
        setUsername={setUsername}
        setAccessKey={setAccessKey}
        remainingCredits={remainingCredits}
        setIsRegisterModalOpen={setIsRegisterModalOpen}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        isRegisterModalOpen={isRegisterModalOpen}
      />
      <div className="rd-dashboard-container">
        <nav className="rd-nav-bar">
          <span className="rd-nav-text">HOME</span>
        </nav>

        <div className="rd-main-content-grid">
          <div className="rd-content-left">
          <div className="rd-kpi-cards-grid">
              <div className="rd-kpi-card rd-kpi-card-red">
                <h2 className="rd-kpi-card-title">{dashboardStats.job_role_created_count}</h2>
                <p className="rd-kpi-card-content">Total Job Roles</p>
              </div>
              <div className="rd-kpi-card rd-kpi-card-teal">
                <h2 className="rd-kpi-card-title">{dashboardStats.resume_uploaded_count}</h2>
                <p className="rd-kpi-card-content">Total Candidates</p>
              </div>
              <div className="rd-kpi-card rd-kpi-card-dark">
                <h2 className="rd-kpi-card-title">{dashboardStats.candidate_shortlisted_count}</h2>
                <p className="rd-kpi-card-content">Total Shortlisted</p>
              </div>
            </div>

            <div className="rd-job-roles-card">
              <div className="rd-job-roles-header">
                <h2 className="rd-job-roles-title">Job Role Latest Results</h2>
                {/*<button onClick={handleShortlistClick}>SHORTLIST CANDIDATES</button>*/}
                <span onClick={handleJobViewClick}>View all</span>
              </div>
              <div className="rd-job-roles-content">
              <div className='rd-table-wrapper'>
                <table className="rd-job-roles-table">
                  <thead>
                    <tr>
                      <th style={{width: "40px"}}>#</th>
                      <th>JOB TITLE</th>
                      {/*<th>JR</th>*/}
                      <th>CANDIDATES</th>
                      <th>SHORTLISTED</th>
                      <th>CREATED</th>
                    </tr>
                  </thead>
                  <tbody>
      {aggregatedItems.map((item, index) => (
        <tr key={index}>
          <td>{index + 1}</td>
          <td style={{color: "#5E1C20", cursor: "pointer"}} 
                          onClick={() => {
                            const matchingJobRole = jobRoles.find(role => role.job_title === (item.jobTitle));
                            navigate(`/job-description?job_role=${matchingJobRole ? matchingJobRole._id : ""}`);
                          }}
                        >{item.jobTitle}</td>
          {/*<td>
            <span
              className="edit-icon"
              onClick={(e) => {
                e.stopPropagation();
                openEditPopup({
                  job_id: item.jobId,
                  jobTitle: item.jobTitle,
                });
              }}
            >
              view
            </span>
          </td>*/}
          <td>{item.numFiles}</td>
          <td>{item.numShortlisted}</td>
          <td>
            {item.createdDate
              ? new Date(item.createdDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : ''}
          </td>
          {/*<td>
            {item.runId && (
              <button
                style={{
                  border: 'none',
                  padding: '0px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  color: '#672024',
                }}
                onClick={() =>
                  navigate(
                    `/results?run_id=${item.runId}&folder_name=${item.folderName}&job_role=${item.jobTitle}&load=false`
                  )
                }
              >
                View Results →
              </button>
            )}
          </td>*/}
        </tr>
      ))}
    </tbody>
                </table></div>
              </div>
            </div>

            <div className="rd-job-roles-card">
              <div className="rd-job-roles-header">
                <h2 className="rd-job-roles-title">Recent Folders</h2>
                <span onClick={handleViewClick}>View all</span>
              </div>
              <div className="rd-job-roles-content">
                <div className='rd-table-wrapper'>
              <table className="rd-job-roles-table">
                  <thead>
                    <tr>
                      <th style={{width: "40px"}}>#</th>
                      <th>FOLDER NAME</th>
                      <th>NO. OF FILES</th>
                      <th>CREATED</th>
                      <th>LAST VIEWED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentFolders.map((folder, index) => (
                      <tr key={folder._id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{folder.folder_name}</td>
                        <td>{folder.num_files || 0} files</td>
                        <td>{folder.created_date ? new Date(folder.created_date).toLocaleDateString() : "Just Now"}</td>                        
                        <td>{folder.last_opened_timestamp ? new Date(folder.last_opened_timestamp).toLocaleDateString() : new Date(folder.created_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            </div>

            {/*<div className="rd-navigation-arrows">
              <button 
                className="rd-btn rd-btn-icon" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <FaChevronLeft className="rd-icon" />
              </button>
              <span>{currentPage} of {totalPages}</span>
              <button 
                className="rd-btn rd-btn-icon"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight className="rd-icon" />
              </button>
            </div>*/}
          </div>
          {/* Recent Activity Sidebar */}
          <div className="rd-sidebar">
          <div className="rd-activity-card">
          <div className="rd-job-roles-header" style={{display: "flex", flexDirection: "column"}}> {/*style={{display: "flex", justifyContent: "center", alignItems: "center"}}*/}
          <h2 className="rd-activity-title" style={{fontSize: "1.1rem"}}>Get Started with MayaMaya Lite</h2>
                <button style={{padding: "12px 50px", marginTop: "0px", width: "fit-content"}} onClick={handleShortlistClick}>GET STARTED</button>
              </div>
          </div>
            <div className="rd-activity-card">
              <h2 className="rd-activity-title">Recent Activity</h2>
              <div className="rd-activity-content">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="rd-activity-item">
                    <p className="rd-activity-text">{activity.activity_name}</p>
                    <span className="rd-activity-time">
                      {new Date(activity.created_date).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
      </div>

      {/*isEditPopupOpen && editableJob && (
        <div className="edit-popup-overlay" onClick={closeEditPopup}>
          <div className="edit-popup" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeEditPopup}>×</button>
            <h3>Edit Job Description</h3>
            <div className="job-form2">
              <div className="form-column">
                <label>Job Title:<span style={{color: "red"}}>*</span></label>
                <input
                  type="text"
                  value={editableJob.job_title}
                  onChange={(e) => setEditableJob({...editableJob, job_title: e.target.value})}
                  placeholder="Enter job title"
                  required
                />

                <label>Degree:<span style={{color: "red"}}>*</span></label>
                <select
                  value={editableJob.job_degree}
                  onChange={(e) => setEditableJob({...editableJob, job_degree: e.target.value})}
                  className='options-popup-dropdown'
                  style={{marginBottom: "10px"}}
                  required
                >
                  <option value="">Other</option>
                  <option value="Bachelor's Degree">Bachelor's</option>
                  <option value="Master's Degree">Master's</option>
                  <option value="Doctoral Degree">PhD</option>
                </select>

                <label>Work Experience:<span style={{color: "red"}}>*</span></label>
                <select 
                  value={convertMonthsToYears(editableJob.job_work_ex)} 
                  onChange={(e) => setEditableJob({...editableJob, job_work_ex: yearsToMonths(e.target.value)})}
                  className='options-popup-dropdown' 
                  required
                >
                  <option value="">Select Experience</option>
                  <option value="0+ years">0+ years</option>
                  <option value="1+ years">1+ years</option>
                  <option value="3+ years">3+ years</option>
                  <option value="5+ years">5+ years</option>
                  <option value="10+ years">10+ years</option>
                </select>

                <label style={{marginTop: "30px"}}>Certifications (if any):</label>
                <input
                  type="text"
                  value={editableJob.job_certificates || ''}
                  onChange={(e) => setEditableJob({...editableJob, job_certificates: e.target.value})}
                  placeholder="Enter any relevant certifications"
                />
              </div>
              <div className="form-column">
                <label>Required Skills:<span style={{color: "red"}}>*</span></label>
                <div className="skills-container">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillInputKeyDownEdit}
                    placeholder="Type a skill and press Enter to add"
                  />
                  <div className="skills-box">
                    {editableJob.job_skills.map((skill, index) => (
                      <div key={index} className="skill-box">
                        {capitalizeFirstLetter(skill)} 
                        <button className="delete-skill-btn" onClick={() => handleDeleteSkill(skill)}>x</button>
                      </div>
                    ))}
                  </div>
                </div>

                <label>Major:<span style={{color: "red"}}>*</span></label>
                <input
                  type="text"
                  value={editableJob.job_major}
                  onChange={(e) => setEditableJob({...editableJob, job_major: e.target.value})}
                  placeholder="e.g., Computer Science, Psychology"
                  required
                />

                <label>Job Responsibilities:<span style={{color: "red"}}>*</span></label>
                <textarea
                  value={editableJob.job_responsibilities}
                  onChange={(e) => setEditableJob({...editableJob, job_responsibilities: e.target.value})}
                  placeholder="Enter your job responsibilities"
                  required
                />
              </div>
            </div>
            <button className="save-btn" onClick={handleEditJobDescription}>Save Changes</button>
          </div>
        </div>
      )*/}

      <footer className="footer"><p>MayaMaya Suite of Products - MayaMaya Lite</p></footer>
      <Bot onLoad={handleBotLoad} />
    </div>
  );
}

